<template>
    <ProductPageLayout :overrides="seoOverrides" :timers="productPageStore.configurableProduct.timers">
        <template #head>
            <Head>
                <link
                    v-if="lcpImage"
                    rel="preload"
                    as="image"
                    :href="lcpImageUrl"
                    :imagesrcset="lcpImage ? buildImageSrcset(lcpImage) : undefined"
                    :imagesizes="productGalleryImageSizes"
                    fetchpriority="high"
                    head-key="product-lcp"
                />

                <!-- Product JSON-LD for rich results (price, rating, availability). -->
                <component :is="'script'" id="lipscore-richsnippet-product" type="application/ld+json" head-key="product-jsonld">{{
                    productJsonLd
                }}</component>
                <component v-if="faqJsonLd" :is="'script'" type="application/ld+json" head-key="faq-jsonld">{{ faqJsonLd }}</component>
            </Head>
        </template>

        <!-- Title and review stars: mobile only, desktop shows them inside the product info panel. -->
        <Container class="mb-4 flex flex-col gap-2 lg:sr-only">
            <!-- The page's only h1: visible on mobile, screen-reader only from lg where the panel shows a copy. -->
            <MobileProductTitle :title="productPageStore.configurableProduct.name" />
            <!-- Tighter star spacing on phones; the label takes the rest of the row and shrinks its text to fit. -->
            <div class="flex items-center justify-between gap-x-2">
                <div v-if="reviewCount !== null && reviewScore !== null" class="shrink-0">
                    <ReviewStars
                        size-mobile="md"
                        :review-count="reviewCount"
                        show-count-as-link
                        :review-score="reviewScore"
                        show-score
                        show-rating
                        count-clickable
                    />
                </div>
                <Labels :labels="productPageStore.configurableProduct.labels" class="flex-1 justify-end" fit />
            </div>
            <!-- Both copies render server-side; after mount only the visible one stays, so the widget loads once. -->
            <TrustedShopsProductTitleWidgets v-if="!isMounted || !isDesktop" class="lg:hidden" />
        </Container>

        <!--
            Headless: owns the Echo subscription, the beauty-shot poll and the credits watchers. These
            used to live in AiUploadButton, which was mounted once per breakpoint — so all of it ran
            twice. The button is now rendered for one breakpoint at a time and may unmount when the
            viewport crosses `md`, which this deliberately outlives.
        -->
        <AiGenerationSessionHost v-if="productStore.isAiProduct" />

        <!--
            Phone-only surface: from `md` up the panel sits beside the gallery instead. Below that,
            it only takes over once a photo is in play — before that the plain product info (price,
            delivery, upload button) already renders inline further down, so there is nothing to
            stand in for pre-mount and no skeleton is needed.
        -->
        <template v-if="productStore.isAiProduct && isMounted && !isTabletUp && showAiMobileSheet">
            <AiMobileSheet />
            <AiMobileTextEditor />
        </template>

        <!-- overflow-x-clip, not hidden: the pinned shortcuts bar bleeds to 100vw, and clip hides that
             overflow without creating a scroll container that would unstick the bar. -->
        <div :class="['space-y-10 overflow-x-clip', { 'pb-36 md:pb-0': productStore.isAiProduct && showAiMobileSheet }]">
            <!-- Render product image gallery and product info -->
            <Container>
                <div class="flex flex-col md:gap-5" :class="productStore.isAiProduct ? 'md:flex-row' : 'lg:flex-row'">
                    <ProductImageGallery />

                    <AiProductInfo
                        v-if="productStore.isAiProduct"
                        :review-count="reviewCount ?? undefined"
                        :review-score="reviewScore ?? undefined"
                    />
                    <ProductInfo v-else :review-count="reviewCount ?? undefined" :review-score="reviewScore ?? undefined" />
                </div>
            </Container>

            <!-- Sticky directly under the page wrapper: a sticky element can only pin within its
                 parent, so it must not get its own wrapper. The sentinel marks when it is stuck. -->
            <div ref="shortcutsSentinel" aria-hidden="true" class="hidden md:block" />
            <div class="sticky top-0 z-30 hidden md:block">
                <div class="container mx-auto px-3">
                    <ProductShortcuts :is-stuck="isShortcutsBarStuck" />
                </div>
            </div>

            <!-- FAQ's and price table: fallback position, shown here when not placed in the Prismic body. -->
            <Container v-if="showFaqs || showPriceTable">
                <div class="mt-10 mb-10 flex flex-col gap-10 md:mt-0 lg:flex-row">
                    <!-- Price table first on mobile; on desktop the FAQs take the left column, or sit centred when alone. -->
                    <ProductFaqs v-if="showFaqs" :centered="!showPriceTable" class="order-2 lg:order-1" />
                    <ProductPriceTable v-if="showPriceTable" class="order-1 lg:order-2" />
                </div>
            </Container>

            <!-- Container's own my-2 beats the space-y-10 above; desktop gets its gap from the shortcuts bar, mobile needs this. -->
            <Container v-if="showReviews" class="max-md:mt-10">
                <ProductReviews />
            </Container>

            <!-- Takes the reviews tab when there are no Lipscore reviews, like the id. -->
            <Container
                v-if="trustedShopsReviews?.count"
                :id="productPageStore.hasReviews ? undefined : 'product-reviews'"
                :data-shortcut-title="productPageStore.hasReviews ? undefined : $t('general.reviews.title')"
            >
                <TrustedShopsProductReviews :reviews="trustedShopsReviews" />
            </Container>

            <!-- Prismic content section -->
            <ProductPrismicSection v-if="page.props.prismic_page" :prismic-page="page.props.prismic_page" class="mb-10" />

            <!-- Same print slider: fallback position, shown here when not placed in the Prismic body. -->
            <Container v-if="showSamePrint">
                <ProductSamePrintSection :items="page.props.same_print_products ?? []" />
            </Container>

            <!-- Related products (collection tag siblings): fallback position, shown here when not placed in the Prismic body. -->
            <Container v-if="showRelated">
                <ProductRelatedSection />
            </Container>

            <!-- Trusted Shops product review widget. -->
            <Container v-if="hasTrustedShopsWidget">
                <TrustedShopsWidget location="product_page" :sku="productSku" :gtin="productGtin" />
            </Container>

            <!-- Hidden Lipscore widget: not shown to users -->
            <LipscoreProductWidget
                :product-name="productPageStore.configurableProduct.name"
                :product-id="lipscoreInternalId ?? null"
                :brand="page.props.store.store_group?.name ?? null"
                :product-url="productPageStore.configurableProduct.canonical_url"
                :sku="productSku"
                :description="productStore.configurableProduct.meta_description"
                :image-url="lipscoreImageUrl"
                :price="lipscorePrice"
                :price-currency="page.props.store.currency"
                :category="lipscoreCategory"
                :mpn="productSku"
                :gtin="productGtin"
            />

            <!-- Render product description -->
            <Container v-if="showDescription">
                <ProductDescription />
            </Container>
        </div>
    </ProductPageLayout>
</template>

<script setup lang="ts">
import Container from '@/components/layouts/Container.vue';
import ProductPageLayout from '@/components/layouts/ProductPageLayout.vue';
import MobileProductTitle from '@/components/product/MobileProductTitle.vue';
import LipscoreProductWidget from '@/components/reviews/LipscoreProductWidget.vue';
import Labels from '@/components/labels/Labels.vue';
import ReviewStars from '@/components/reviews/ReviewStars.vue';
import TrustedShopsProductTitleWidgets from '@/components/reviews/TrustedShopsProductTitleWidgets.vue';
import TrustedShopsWidget from '@/components/reviews/TrustedShopsWidget.vue';
import { useAppearanceComposable } from '@/composables/appearance/AppearanceComposable';
import { useJoostElements } from '@/composables/product-page/useJoostElements';
import { selectSavedWishlistSize } from '@/composables/products/ProductWishlistComposable';
import { buildFaqPageJsonLd } from '@/composables/seo/useFaqPageJsonLd';
import { serializeJsonLd } from '@/composables/seo/useJsonLd';
import { buildProductJsonLd, resolveCategory } from '@/composables/seo/useProductJsonLd';
import { taggrsItemBrand } from '@/composables/taggrsBrand';
import { taggrsCategoryFields } from '@/composables/taggrsCategoryFields';
import { resolveTaggrsItemId } from '@/composables/taggrsItemMapper';
import { useCheckoutUserData } from '@/composables/useCheckoutUserData';
import { pushViewItem, type EcommerceItem } from '@/composables/useDataLayer';
import AiProductInfo from '@/pages/ai-product-page/partials/ai-product-info/AiProductInfo.vue';
import AiGenerationSessionHost from '@/pages/ai-product-page/partials/ai-upload/AiGenerationSessionHost.vue';
import AiMobileSheet from '@/pages/ai-product-page/partials/AiMobileSheet.vue';
import AiMobileTextEditor from '@/pages/ai-product-page/partials/AiMobileTextEditor.vue';
import { useAiPanelState } from '@/composables/ai-upload/useAiPanelState';
import { whenUrlFilterSkusLoaded } from '@/lib/products/urlFilterSkus';
import { parseFilterParamsFromSearch } from '@/lib/products/urlFilters';
import { sizeChosenParam } from '@/lib/urls';
import ProductDescription from '@/pages/product-page/partials/product-description/ProductDescription.vue';
import ProductFaqs from '@/pages/product-page/partials/product-faqs/ProductFaqs.vue';
import ProductImageGallery from '@/pages/product-page/partials/product-image-gallery/ProductImageGallery.vue';
import ProductInfo from '@/pages/product-page/partials/product-info/ProductInfo.vue';
import ProductPriceTable from '@/pages/product-page/partials/product-price-table/ProductPriceTable.vue';
import ProductPrismicSection from '@/pages/product-page/partials/product-prismic/ProductPrismicSection.vue';
import ProductRelatedSection from '@/pages/product-page/partials/product-related/ProductRelatedSection.vue';
import ProductSamePrintSection from '@/pages/product-page/partials/product-same-print/ProductSamePrintSection.vue';
import ProductReviews from '@/pages/product-page/partials/product-reviews/ProductReviews.vue';
import TrustedShopsProductReviews from '@/pages/product-page/partials/product-reviews/TrustedShopsProductReviews.vue';
import ProductShortcuts from '@/pages/product-page/partials/product-shortcuts/ProductShortcuts.vue';
import { useProductPageStore } from '@/stores/pages/ProductPageStore';
import { useProductNavigationStore } from '@/stores/products/ProductNavigationStore';
import { useProductStore } from '@/stores/products/ProductStore';
import { useMediaclipSessionStore } from '@/stores/tool/MediaclipSessionStore';
import type { AppPageProps } from '@/types';
import type {
    CollectionSliderItemObject,
    ConfigurableProductObject,
    ConfigurableProductProductObject,
    LipscoreProductObject,
    LipscoreProductReviewObject,
    PaazlDeliveryTimeObject,
    PaazlShippingDataObject,
    SamePrintProductObject,
    StoreKlarnaObject,
} from '@/types/generated';
import type { SeoOverrides } from '@/types/seo';
import { absoluteImageUrl, buildImageSrcset, ImageTypeEnum, productGalleryImageSizes } from '@/utils/imageUrl';
import { Head, usePage } from '@inertiajs/vue3';
import _ from 'lodash';
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';

type ProductIndexPageProps = {
    product: ConfigurableProductObject;
    lipscore_product?: LipscoreProductObject | null;
    reviews?: LipscoreProductReviewObject[] | null;
    shipping?: PaazlShippingDataObject | null;
    shipping_delivery_times?: PaazlDeliveryTimeObject[] | null;
    klarna?: StoreKlarnaObject;
    taggrs_item_id?: string;
    taggrs_categories?: string[];
};

const props = defineProps<ProductIndexPageProps>();

const { isDesktop, isTabletUp } = useAppearanceComposable();
const isMounted = ref(false);
const { generationStarted, showPhotoSelectionBlock } = useAiPanelState();

// A carried photo from a related style, primed but not yet generated, also needs the sheet — it is
// where the customer taps "Generate" and sees the photo they are about to use.
const showAiMobileSheet = computed<boolean>(() => generationStarted.value || showPhotoSelectionBlock.value);
const page = usePage<
    AppPageProps & {
        prismic_page?: Record<string, any> | null;
        same_print_products?: SamePrintProductObject[];
        related_products?: CollectionSliderItemObject[];
        /** Per url filter, the skus whose Algolia variant holds its value. */
        url_filter_skus?: Record<string, string[]>;
    }
>();
const productPageStore = useProductPageStore();
const productStore = useProductStore();
const sessionStore = useMediaclipSessionStore();
const productNavigationStore = useProductNavigationStore();
const shouldResetState = page.props.product_page_reset_state === true || (productStore.isMediaclipConfiguratorProduct && !sessionStore.isActive);

const { ratesWithTrustedShops, trustedShopsReviews, reviewScore, reviewCount } = productPageStore;

const { isPlacedInPrismic } = useJoostElements();

// A section is skipped at its hardcoded position when the editor placed it in the Prismic body.
const showFaqs = computed((): boolean => !isPlacedInPrismic('FAQs'));
// A set or meerluik has one price of its own, so the per-size table would only contradict it.
const showPriceTable = computed(
    (): boolean => !productPageStore.configurableProduct.price_table_hidden && !productStore.isSetBackedProduct && !isPlacedInPrismic('PriceTable'),
);
const showReviews = computed((): boolean => productPageStore.hasReviews && !isPlacedInPrismic('LipscoreReviews'));
// The sliders guard on `items.length` themselves; repeated here so an empty one does not leave its
// Container behind as a gap.
const showSamePrint = computed(
    (): boolean =>
        productPageStore.configurableProduct.same_print_slider_enabled &&
        !isPlacedInPrismic('SamePrintSlider') &&
        !_.isEmpty(page.props.same_print_products),
);
const showRelated = computed((): boolean => !isPlacedInPrismic('RelatedProducts') && !_.isEmpty(page.props.related_products));
const showDescription = computed((): boolean => !isPlacedInPrismic('ProductDescription'));

// Mirrors the widget's own guard, so an unconfigured store leaves no empty Container behind.
const hasTrustedShopsWidget = computed(() => !trustedShopsReviews && !!page.props.trusted_shops?.widgets.product_page);

// First gallery image, so the preload/OG/JSON-LD image matches what is shown (set images included).
const lcpImage = computed(() => productStore.selectedProductImages[0] ?? null);

// Absolute, so the preload and og:image point at the exact same URL.
const lcpImageUrl = computed((): string | undefined =>
    lcpImage.value ? absoluteImageUrl(lcpImage.value, ImageTypeEnum.Laptop, page.props.store.clean_root_url) : undefined,
);

const productSku = computed(() => productStore.configurableProduct.group_identifier ?? null);
const productGtin = computed(() => productStore.configurableProduct.default_configurable_product_product?.metadata?.ean ?? null);
const lipscoreCategory = computed(() => resolveCategory(page.props.seo?.breadcrumbs ?? []));

// Must match invitation `internal_id` so Lipscore links page views and reviews to the same product.
const lipscoreInternalId = computed(() => productStore.configurableProduct.generation_group_parent_name ?? undefined);

const lipscoreImageUrl = computed((): string | null => {
    if (!lcpImage.value) {
        return null;
    }

    return absoluteImageUrl(lcpImage.value, ImageTypeEnum.Full, page.props.store.clean_root_url, ImageTypeEnum.Full) ?? null;
});

const lipscorePrice = computed(
    (): number | null => productStore.configurableProduct.default_configurable_product_product?.product?.data?.product_price ?? null,
);

const seoOverrides = computed((): SeoOverrides => ({
    title: productPageStore.configurableProduct.meta_title,
    description: productPageStore.configurableProduct.meta_description ?? null,
    canonical: productPageStore.configurableProduct.canonical_url,
    open_graph: {
        type: 'product',
        title: productPageStore.configurableProduct.meta_title,
        description: productPageStore.configurableProduct.meta_description ?? null,
        url: productPageStore.configurableProduct.canonical_url,
        image: lcpImageUrl.value ?? null,
    },
    product_price: lipscorePrice.value !== null ? { amount: lipscorePrice.value, currency: page.props.store.currency } : null,
}));

const productJsonLd = computed((): string | null =>
    serializeJsonLd(
        buildProductJsonLd({
            configurableProduct: productStore.configurableProduct,
            images: productStore.selectedProductImages,
            lipscoreProduct: productPageStore.lipscoreProduct ?? null,
            reviews: showReviews.value ? productPageStore.reviews : [],
            breadcrumbs: page.props.seo?.breadcrumbs ?? [],
            store: page.props.store,
            deliveryTimes: props.shipping_delivery_times ?? null,
            ratesWithTrustedShops,
            trustedShopsReviews,
        }),
    ),
);

const faqJsonLd = computed((): string | null => serializeJsonLd(buildFaqPageJsonLd(productStore.configurableProduct.faqs ?? [])));

// Capture the persisted selection before resetting — Pinia's persistedstate plugin
// loads it synchronously from localStorage before this code runs. We use it in
// onMounted (post-hydration) to restore a previously confirmed size and filter
// options for force_size_selection products without causing an SSR hydration mismatch.
const persistedProductSku = productStore.selectedProduct?.product?.sku;
const persistedFilterOptions = { ...productStore.selectedFilterOptions };

// Always reset product selection to defaults so the initial client render
// matches the SSR HTML. pinia-plugin-persistedstate restores selectedProduct
// from localStorage synchronously before Vue compares against the SSR HTML,
// but SSR always uses the default product — so any stored selection produces
// a hydration mismatch. Resetting unconditionally here ensures both sides
// start from the same state during hydration.
productStore.resetToDefaults();

if (shouldResetState) {
    sessionStore.clear();
}

onMounted(() => {
    isMounted.value = true;

    // The lister page arrived here with no filters/sku of its own active, so any persisted
    // selection from an earlier, unrelated visit must not leak into this one.
    const shouldSkipPersistedSelection = productNavigationStore.consumeSkipPersistedSelection(window.location.pathname);

    if (shouldSkipPersistedSelection) {
        productStore.$persist();
    }

    removeUrlFilterSkusListener = whenUrlFilterSkusLoaded(page, () => restoreSelection(shouldSkipPersistedSelection));
});

let removeUrlFilterSkusListener: (() => void) | undefined;
let stopSavedWishlistSize: (() => void) | undefined;

onBeforeUnmount(() => {
    removeUrlFilterSkusListener?.();
    stopSavedWishlistSize?.();
});

function restoreSelection(shouldSkipPersistedSelection: boolean): void {
    applyUrlFiltersFromUrl();

    // A sku in the URL is explicit intent and outranks any persisted or session selection.
    const hasUrlSku = applySkuSelectionFromUrl();

    if (!hasUrlSku && !shouldSkipPersistedSelection) {
        restorePersistedSizeSelection(persistedProductSku);
    }

    pushViewItemEvent();

    if (!hasUrlSku) {
        stopSavedWishlistSize = selectSavedWishlistSize(page);
    }

    if (shouldResetState) {
        destroyMediaclipHub();
        return;
    }

    // The url already named a size; restoring the launched one would silently
    // replace it, and drag its filter options over the visitor's selection.
    if (hasUrlSku) {
        return;
    }

    // Session is active — restore the product the user launched Mediaclip
    // with so the UI reflects their active editing context after hydration.
    restoreSessionProduct();
}

// The bar widens its background to the full viewport only while actually pinned.
const shortcutsSentinel = useTemplateRef('shortcutsSentinel');
const isShortcutsBarStuck = ref(false);
let shortcutsObserver: IntersectionObserver | undefined;

onMounted(() => {
    if (!shortcutsSentinel.value) {
        return;
    }

    shortcutsObserver = new IntersectionObserver(
        (entries: IntersectionObserverEntry[]): void => {
            // The sentinel is also out of view below the fold, so the bar only counts as stuck once
            // the sentinel has left through the top edge.
            isShortcutsBarStuck.value = entries.some((entry: IntersectionObserverEntry) => !entry.isIntersecting && entry.boundingClientRect.top < 0);
        },
        // The bar sits my-10 (40px) below the sentinel; stretch the root upward by the same
        // amount so "sentinel gone" lines up exactly with the moment the bar pins.
        { rootMargin: '40px 0px 0px 0px' },
    );

    shortcutsObserver.observe(shortcutsSentinel.value);
});

onBeforeUnmount(() => shortcutsObserver?.disconnect());

function pushViewItemEvent(): void {
    const item = buildViewItem();

    pushViewItem(
        {
            currency: page.props.store.currency,
            value: item.price,
            items: [item],
        },
        useCheckoutUserData(),
        null,
        page.props.store?.site,
    );
}

function resolveViewItemData() {
    const configurable = productPageStore.configurableProduct;
    const defaultProduct = configurable.default_configurable_product_product;
    const selectedProduct = productStore.selectedProduct?.product ?? defaultProduct?.product;

    return {
        configurable,
        defaultProduct,
        selectedProduct,
        variantSku: selectedProduct?.sku ?? null,
        groupIdentifier: props.taggrs_item_id ?? configurable.group_identifier ?? '',
    };
}

function buildViewItem(): EcommerceItem {
    const { configurable, defaultProduct, selectedProduct, variantSku, groupIdentifier } = resolveViewItemData();
    const itemId = resolveTaggrsItemId(groupIdentifier) ?? configurable.name;
    const price = selectedProduct?.data?.product_price ?? defaultProduct?.product?.data?.product_price ?? 0;

    return {
        item_id: itemId,
        item_name: configurable.name,
        item_brand: taggrsItemBrand(),
        price,
        ...(variantSku && variantSku !== itemId ? { item_variant: variantSku } : {}),
        ...taggrsCategoryFields(props.taggrs_categories ?? []),
    };
}

function restorePersistedSizeSelection(sku: string | undefined): void {
    if (!productStore.configurableProduct.force_size_selection) {
        return;
    }

    if (productStore.hasUserSelectedProduct) {
        return;
    }

    if (!sku) {
        return;
    }

    const defaultSku = productStore.configurableProduct.default_configurable_product_product?.product?.sku;

    if (sku === defaultSku) {
        return;
    }

    const matched = productStore.configurableProduct.configurable_product_products.find(
        (p: ConfigurableProductProductObject): boolean => p.product.sku === sku,
    );

    if (!matched) {
        return;
    }

    productStore.applyProduct(matched);

    // Restore the persisted filter selections on top of the product defaults.
    // applyProduct sets filter options from the product's data; the user's
    // explicit previous choices may differ, so we overlay them here.
    productStore.selectedFilterOptions = { ...productStore.selectedFilterOptions, ...persistedFilterOptions };
}

function restoreSessionProduct(): void {
    const launchedSku = sessionStore.launchedProductSku;

    if (!launchedSku) {
        return;
    }

    const matchingProduct = productStore.configurableProduct.configurable_product_products.find(
        (p: ConfigurableProductProductObject): boolean => p.product.sku === launchedSku,
    );

    if (matchingProduct === undefined) {
        return;
    }

    productStore.applyProduct(matchingProduct);
}

function destroyMediaclipHub(): void {
    if (!window.mediaclip) {
        return;
    }

    window.mediaclip.hub.destroy(true);
}

/**
 * Restrict the selectable sizes to the filter params carried over from a lister page
 * (e.g. ?filters[Formaat classificatie]=XXL). Runs post-hydration, like the sku handling:
 * SSR renders the unrestricted defaults, so touching this earlier would desync the markup.
 */
function applyUrlFiltersFromUrl(): void {
    if (typeof window === 'undefined') {
        return;
    }

    const urlFilters = parseFilterParamsFromSearch(window.location.search);

    if (_.isEmpty(urlFilters)) {
        return;
    }

    productStore.setUrlFilters(urlFilters, page.props.url_filter_skus ?? {});

    // Re-derive the default product and filter options from the restricted set.
    productStore.resetToDefaults();
}

function applySkuSelectionFromUrl(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const sku = searchParams.get('sku');
    if (sku === null || sku.length === 0) {
        return false;
    }

    // Runs for any requested sku, including one this product does not carry: if the
    // filters do not match it, they can only leave the visitor on a list that cannot
    // contain the size they asked for.
    productStore.dropUrlFiltersExcludingSku(sku);

    const matchedProduct = productStore.configurableProduct.configurable_product_products.find(
        (configurableProductProduct: ConfigurableProductProductObject): boolean => configurableProductProduct.product.sku === sku,
    );
    if (matchedProduct === undefined) {
        // No product to preset, so re-derive the defaults from whatever the filters left.
        productStore.resetToDefaults();

        return false;
    }

    // A size saved on the wishlist was already chosen, so it lifts the force_size_selection gate.
    if (searchParams.get(sizeChosenParam) === '1') {
        productStore.applyProduct(matchedProduct);

        return true;
    }

    // Preset rather than apply: the size is selected, but force_size_selection
    // products keep their gate until the user confirms in the dropdown.
    productStore.presetProduct(matchedProduct);

    return true;
}
</script>
